import React, { useState, useCallback, useMemo } from 'react';
import { Type, Copy, Check, RefreshCw } from 'lucide-react';

type GenerateType = 'paragraphs' | 'sentences' | 'words';

const LOREM_WORDS = [
  'lorem','ipsum','dolor','sit','amet','consectetur','adipiscing','elit','sed','do',
  'eiusmod','tempor','incididunt','ut','labore','et','dolore','magna','aliqua','enim',
  'ad','minim','veniam','quis','nostrud','exercitation','ullamco','laboris','nisi',
  'aliquip','ex','ea','commodo','consequat','duis','aute','irure','in','reprehenderit',
  'voluptate','velit','esse','cillum','fugiat','nulla','pariatur','excepteur','sint',
  'occaecat','cupidatat','non','proident','sunt','culpa','qui','officia','deserunt',
  'mollit','anim','id','est','laborum','ac','accumsan','aliquet','ante','arcu','at',
  'auctor','augue','bibendum','blandit','condimentum','congue','cras','curabitur',
  'cursus','dapibus','diam','dictum','dignissim','donec','dui','efficitur','egestas',
  'elementum','eleifend','erat','eros','euismod','facilisi','fames','faucibus',
  'felis','fermentum','feugiat','finibus','gravida','habitant','habitasse','hac',
  'hendrerit','iaculis','imperdiet','integer','interdum','justo','lacinia','lacus',
  'laoreet','lectus','leo','libero','ligula','lobortis','luctus','maecenas','massa',
  'mattis','mauris','maximus','metus','mi','molestie','morbi','nam','nec','neque',
  'nibh','nunc','odio','orci','ornare','pellentesque','pharetra','phasellus','placerat',
  'platea','porta','porttitor','posuere','praesent','pretium','proin','pulvinar',
  'purus','quam','quisque','rhoncus','risus','rutrum','sagittis','sapien','scelerisque',
  'semper','senectus','sodales','sollicitudin','suscipit','suspendisse','tellus',
  'tincidunt','tortor','tristique','turpis','ultrices','ultricies','urna','varius',
  'vehicula','vel','vestibulum','vitae','vivamus','viverra','volutpat','vulputate',
  'aenean','aptent','class','conubia','convallis','dis','etiam','fringilla','fusce',
  'himenaeos','inceptos','litora','magnis','malesuada','montes','nascetur','natoque',
  'nostra','parturient','penatibus','per','ridiculus','sociis','sociosqu','taciti',
  'torquent','mus','netus','primis',
];

const CLASSIC_START = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. ';

const HEADINGS = [
  'Mauris Elementum','Praesent Volutpat','Curabitur Sagittis','Vestibulum Ante',
  'Nulla Facilisi','Donec Ultricies','Pellentesque Habitant','Fusce Dapibus',
  'Aenean Commodo','Vivamus Vestibulum',
];

const LoremIpsumGenerator: React.FC = () => {
  const [genType, setGenType] = useState<GenerateType>('paragraphs');
  const [count, setCount] = useState(3);
  const [startClassic, setStartClassic] = useState(true);
  const [htmlTags, setHtmlTags] = useState(false);
  const [includeHeadings, setIncludeHeadings] = useState(false);
  const [output, setOutput] = useState('');
  const [copied, setCopied] = useState(false);
  const [seed, setSeed] = useState(0);

  const randomWord = useCallback(() => LOREM_WORDS[Math.floor(Math.random() * LOREM_WORDS.length)], []);

  const generateSentence = useCallback((wordCount?: number): string => {
    const len = wordCount || (5 + Math.floor(Math.random() * 11)); // 5-15 words
    const words: string[] = [];
    for (let i = 0; i < len; i++) words.push(randomWord());
    words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
    return words.join(' ') + '.';
  }, [randomWord]);

  const generateParagraph = useCallback((): string => {
    const sentCount = 3 + Math.floor(Math.random() * 5); // 3-7 sentences
    const sentences: string[] = [];
    for (let i = 0; i < sentCount; i++) sentences.push(generateSentence());
    return sentences.join(' ');
  }, [generateSentence]);

  const generate = useCallback(() => {
    let result = '';
    const capped = Math.min(50, Math.max(1, count));

    if (genType === 'words') {
      const words: string[] = [];
      for (let i = 0; i < capped; i++) words.push(randomWord());
      if (startClassic && capped >= 5) {
        words[0] = 'lorem'; words[1] = 'ipsum'; words[2] = 'dolor'; words[3] = 'sit'; words[4] = 'amet';
      }
      words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
      result = words.join(' ');
    } else if (genType === 'sentences') {
      const sentences: string[] = [];
      for (let i = 0; i < capped; i++) sentences.push(generateSentence());
      if (startClassic && sentences.length > 0) {
        sentences[0] = CLASSIC_START.trim();
      }
      result = sentences.join(' ');
    } else {
      // paragraphs
      const paragraphs: string[] = [];
      const usedHeadings = [...HEADINGS].sort(() => Math.random() - 0.5);
      for (let i = 0; i < capped; i++) {
        let para = generateParagraph();
        if (startClassic && i === 0) {
          para = CLASSIC_START + para;
        }
        if (includeHeadings && i > 0 && i % 2 === 0 && usedHeadings.length > 0) {
          const heading = usedHeadings.pop()!;
          const level = Math.random() > 0.5 ? 'h2' : 'h3';
          if (htmlTags) {
            para = `<${level}>${heading}</${level}>\n<p>${para}</p>`;
          } else {
            para = `${heading}\n\n${para}`;
          }
        } else if (htmlTags) {
          para = `<p>${para}</p>`;
        }
        paragraphs.push(para);
      }
      result = paragraphs.join(htmlTags ? '\n\n' : '\n\n');
    }

    setOutput(result);
    setSeed(s => s + 1);
  }, [genType, count, startClassic, htmlTags, includeHeadings, generateSentence, generateParagraph, randomWord]);

  // Auto-generate on first mount and param changes
  const outputStats = useMemo(() => {
    if (!output) return { words: 0, chars: 0 };
    const plainText = output.replace(/<[^>]+>/g, '');
    return {
      words: plainText.trim() ? plainText.trim().split(/\s+/).length : 0,
      chars: plainText.length,
    };
  }, [output]);

  const copyOutput = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-emerald-100 rounded-lg">
          <Type className="w-6 h-6 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lorem Ipsum Generator</h1>
          <p className="text-gray-500 text-sm">Generate placeholder text for your designs</p>
        </div>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-2">Type</label>
          <div className="flex gap-2">
            {(['paragraphs', 'sentences', 'words'] as GenerateType[]).map(t => (
              <button
                key={t}
                onClick={() => setGenType(t)}
                className={`px-3 py-1.5 text-sm rounded-lg capitalize ${
                  genType === t ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 block mb-2">Count (1-50)</label>
          <input
            type="number"
            min={1}
            max={50}
            value={count}
            onChange={(e) => setCount(Math.min(50, Math.max(1, Number(e.target.value))))}
            className="w-24 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-emerald-500"
          />
        </div>
      </div>

      {/* Options */}
      <div className="flex flex-wrap gap-4 mb-6">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={startClassic}
            onChange={(e) => setStartClassic(e.target.checked)}
            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span className="text-gray-700">Start with &quot;Lorem ipsum...&quot;</span>
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={htmlTags}
            onChange={(e) => setHtmlTags(e.target.checked)}
            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span className="text-gray-700">Wrap in HTML tags</span>
        </label>
        {genType === 'paragraphs' && (
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={includeHeadings}
              onChange={(e) => setIncludeHeadings(e.target.checked)}
              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-gray-700">Include random headings</span>
          </label>
        )}
      </div>

      {/* Generate button */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={generate}
          className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
        >
          Generate
        </button>
        {output && (
          <>
            <button
              onClick={generate}
              className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm flex items-center gap-1"
            >
              <RefreshCw className="w-4 h-4" /> Shuffle
            </button>
            <button
              onClick={copyOutput}
              className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm flex items-center gap-1"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </>
        )}
      </div>

      {/* Output */}
      {output && (
        <>
          <textarea
            readOnly
            value={output}
            className="w-full h-64 p-4 border border-gray-300 rounded-lg bg-gray-50 text-gray-800 text-sm resize-y font-mono"
          />

          <div className="mt-3 flex gap-4 text-sm text-gray-500">
            <span>{outputStats.words} words</span>
            <span>{outputStats.chars} characters</span>
          </div>
        </>
      )}
    </div>
  );
};

export default LoremIpsumGenerator;
